<script>
import Card from '@/Components/Card.vue';
import { formatDate } from '../../../Modules/Dates.js';
import ToggleSwitch from '@/Components/Inputs/ToggleSwitch.vue';
import Modal from '@/Components/Modal.vue';

const MODAL_ID = 'scheduler-recommendation-media';

export default {
  components: { ToggleSwitch, Card, Modal },

  props: {
    masterConfig: Object,
    kioskKey: String,
    recommendations: {
      type: Array,
      default: () => [],
    },
    inspec_images: {
      type: Object,
      default: () => ({}),
    },
    inspec_videos: {
      type: Object,
      default: () => ({}),
    },
    customer: Object,
    customerRecommendations: {
      type: [Array, Object],
      default: () => ({}),
    },
  },
  data() {
    return {
      modalId: MODAL_ID,
      attachment: null,
      modal: null,
    };
  },

  methods: {
    formatDate,

    recommendationSelected(recommendation, selected) {
      let recommendations = { ...(this.customerRecommendations ?? {}) };

      // Use inspec_id instead of results_id
      let existingKey = Object.keys(recommendations).find(
        (key) => recommendations[key].inspec_id === recommendation.inspec_id,
      );

      if (!existingKey && selected) {
        // Use inspec_id as the key
        recommendations[recommendation.inspec_id] = recommendation;
        this.$emit('recommendation', recommendations);
        return;
      }

      if (!selected && existingKey) {
        delete recommendations[existingKey];
      }

      this.$emit('recommendation', recommendations);
    },

    /**
     * Photos and videos recorded against the item on the prior visit, in the
     * same shape the DVI item attachment strip consumes.
     */
    recommendationMedia(recommendation) {
      const inspecId = recommendation?.item?.inspec_id;
      if (!inspecId) {
        return [];
      }

      const images = this.inspec_images?.[inspecId];
      const videos = this.inspec_videos?.[inspecId];

      return [
        ...(Array.isArray(images) ? images : []),
        ...(Array.isArray(videos) ? videos : []),
      ].filter((media) => this.mediaSrc(media));
    },

    mediaType(media) {
      const type = String(media?.attachment_type || '').toLowerCase();
      if (type) return type;
      if (media?.video_id || media?.video_url || media?.video_file) return 'video';
      return 'image';
    },

    isVideo(media) {
      return this.mediaType(media) === 'video';
    },

    /** Poster frame for videos, thumbnail for photos. */
    mediaThumbnail(media) {
      if (this.isVideo(media)) {
        return media?.poster_url || media?.thumbnail_url || '';
      }

      return media?.thumbnail_url || media?.image_url || media?.url || '';
    },

    /** Full-size source used inside the viewer. */
    mediaSrc(media) {
      if (this.isVideo(media)) {
        return media?.video_url || '';
      }

      return media?.image_url || media?.url || media?.thumbnail_url || '';
    },

    mediaKey(media, index) {
      return media?.attachment_id ?? media?.image_id ?? media?.video_id ?? index;
    },

    mediaLabel(media, recommendation, index) {
      const item = recommendation?.item?.inspec_name ?? 'inspected item';
      return `${this.isVideo(media) ? 'Play video' : 'View photo'} ${index + 1} for ${item}`;
    },

    hideBrokenThumbnail(event) {
      event.currentTarget.hidden = true;
    },

    openMedia(media) {
      this.attachment = media;
      this.modal?.modal('show');
    },

    reset() {
      const video = this.$refs.videoPlayer;
      if (video?.pause) {
        video.pause();
      }

      this.attachment = null;
    },
  },

  emits: ['recommendation', 'ready'],

  mounted() {
    this.$emit('ready', true);
    this.modal = $('#' + this.modalId);
  },

  computed: {
    modalTitle() {
      if (!this.attachment) {
        return '';
      }

      return this.isVideo(this.attachment) ? 'Inspection Video' : 'Inspection Photo';
    },
  },
};
</script>

<template>
  <Card class="mt-3">
    <h3 class="mb-3">Previously Inspected Items:</h3>
    <h5 class="mb-3">
      We found some recommended items from your last visit. Check any you would like us to review:
    </h5>

    <div class="table-responsive">
      <table class="table table-striped align-middle">
        <thead>
          <tr>
            <th>Item</th>
            <th>Inspected On</th>
            <th>Technician Notes</th>
            <th>Images</th>
            <td></td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="recommendation in recommendations" :key="recommendation.inspec_id">
            <td>{{ recommendation?.item?.inspec_name }}</td>
            <td class="text-nowrap">{{ formatDate(recommendation.date_time) }}</td>
            <td>{{ recommendation.notes }}</td>
            <td>
              <div
                class="recommendation-media"
                v-if="recommendationMedia(recommendation).length > 0"
                aria-label="Previous inspection media"
              >
                <button
                  type="button"
                  class="recommendation-media__tile"
                  v-for="(media, index) in recommendationMedia(recommendation)"
                  :key="mediaKey(media, index)"
                  :title="mediaLabel(media, recommendation, index)"
                  :aria-label="mediaLabel(media, recommendation, index)"
                  @click="openMedia(media)"
                >
                  <span class="recommendation-media__fallback" aria-hidden="true">
                    <i :class="isVideo(media) ? 'fa fa-video' : 'fa fa-image'"></i>
                  </span>
                  <img
                    v-if="mediaThumbnail(media)"
                    class="recommendation-media__image"
                    :src="mediaThumbnail(media)"
                    alt=""
                    loading="lazy"
                    @error="hideBrokenThumbnail"
                  />
                  <span v-if="isVideo(media)" class="recommendation-media__play" aria-hidden="true">
                    <i class="fa fa-play"></i>
                  </span>
                </button>
              </div>
            </td>
            <td>
              <ToggleSwitch
                @update:modelValue="(newValue) => recommendationSelected(recommendation, newValue)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Card>

  <Modal :id="modalId" :title="modalTitle" size="lg" @closed="reset">
    <video
      v-if="attachment && isVideo(attachment)"
      ref="videoPlayer"
      class="recommendation-media__viewer"
      :src="mediaSrc(attachment)"
      :poster="mediaThumbnail(attachment)"
      controls
      controlslist="nodownload"
      playsinline
      preload="metadata"
    ></video>
    <img
      v-else-if="attachment"
      class="recommendation-media__viewer"
      :src="mediaSrc(attachment)"
      alt=""
    />
  </Modal>
</template>

<style scoped>
.recommendation-media {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
}

.recommendation-media__tile {
  position: relative;
  display: inline-flex;
  flex: 0 0 72px;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--bs-border-color);
  border-radius: 7px;
  background: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
  cursor: pointer;
}

.recommendation-media__tile:focus-visible {
  outline: 2px solid var(--bs-primary);
  outline-offset: 1px;
}

.recommendation-media__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  line-height: 1;
}

.recommendation-media__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recommendation-media__play {
  position: absolute;
  top: 50%;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.68);
  color: #fff;
  font-size: 0.7rem;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.recommendation-media__play i {
  margin: 0 0 0 1px !important;
}

.recommendation-media__viewer {
  display: block;
  width: 100%;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  object-fit: contain;
}
</style>
